import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import type {
    SearchEntityType,
    SearchResultItem,
    UnifiedSearchResults,
    UnifiedSearchTotals,
} from "../../services/apis/search.api";

export interface RecentSearch {
    q: string;
    at: number;
    url: string;
    title: string;
    type: SearchEntityType;
    openTaskId?: string;
}

export interface SearchState {
    open: boolean;
    query: string;
    loading: boolean;
    results: UnifiedSearchResults | null;
    totals: UnifiedSearchTotals | null;
    error: string | null;
    recent: RecentSearch[];
    recentHydrated: boolean;
}

const initialState: SearchState = {
    open: false,
    query: "",
    loading: false,
    results: null,
    totals: null,
    error: null,
    recent: [],
    recentHydrated: false,
};

const searchSlice = createSlice({
    name: "search",
    initialState,
    reducers: {
        openPalette(state) {
            state.open = true;
        },
        closePalette(state) {
            state.open = false;
            state.query = "";
            state.results = null;
            state.totals = null;
            state.error = null;
            state.loading = false;
        },
        togglePalette(state) {
            const willOpen = !state.open;
            state.open = willOpen;
            if (!willOpen) {
                state.query = "";
                state.results = null;
                state.totals = null;
                state.error = null;
                state.loading = false;
            }
        },
        setQuery(state, action: PayloadAction<string>) {
            state.query = action.payload;
            if (!action.payload.trim()) {
                state.results = null;
                state.totals = null;
                state.error = null;
            }
        },
        searchStart(state) {
            state.loading = true;
            state.error = null;
        },
        searchSuccess(
            state,
            action: PayloadAction<{
                results: UnifiedSearchResults;
                totals: UnifiedSearchTotals;
            }>
        ) {
            state.loading = false;
            state.results = action.payload.results;
            state.totals = action.payload.totals;
            state.error = null;
        },
        searchFailure(state, action: PayloadAction<string>) {
            state.loading = false;
            state.error = action.payload;
        },
        hydrateRecent(state, action: PayloadAction<RecentSearch[]>) {
            state.recent = action.payload.slice(0, 5);
            state.recentHydrated = true;
        },
        pushRecent(state, action: PayloadAction<RecentSearch>) {
            const incoming = action.payload;
            const deduped = state.recent.filter((r) => r.url !== incoming.url);
            state.recent = [incoming, ...deduped].slice(0, 5);
        },
        clearRecent(state) {
            state.recent = [];
        },
    },
});

export const {
    openPalette,
    closePalette,
    togglePalette,
    setQuery,
    searchStart,
    searchSuccess,
    searchFailure,
    hydrateRecent,
    pushRecent,
    clearRecent,
} = searchSlice.actions;

export const selectSearch = (state: RootState) => state.search;
export const searchReducer = searchSlice.reducer;
export default searchSlice.reducer;

// Helpers for localStorage-backed recent searches
const LS_PREFIX = "p3m:recentSearches:";
function recentKey(userId: string | null | undefined): string {
    return `${LS_PREFIX}${userId || "anon"}`;
}
export function loadRecentFromStorage(userId: string | null | undefined): RecentSearch[] {
    try {
        const raw = localStorage.getItem(recentKey(userId));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.slice(0, 5) : [];
    } catch {
        return [];
    }
}
export function persistRecentToStorage(
    userId: string | null | undefined,
    items: RecentSearch[]
): void {
    try {
        localStorage.setItem(recentKey(userId), JSON.stringify(items.slice(0, 5)));
    } catch {
        /* ignore */
    }
}
export function clearRecentFromStorage(userId: string | null | undefined): void {
    try {
        localStorage.removeItem(recentKey(userId));
    } catch {
        /* ignore */
    }
}

export type { SearchResultItem };
